import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { defaultModalContentProps, Modal } from './Modal.js';
import { useWindowPanning } from '../../hooks/useWindowPanning.js';
import { cssVar } from '../../theme/variables.js';
import { Content } from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import React__default, { useRef, useMemo } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { keyframes, css, styled } from 'styled-components';
import useMinContentHeight from '@fabric-app/hooks/useMinContentHeight';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { mediaNotMobile, mediaMobile } from '../../constants/styled-utils.js';

const inAnimation = keyframes([
  `from{opacity:0;transform:translateY(100%);}to{opacity:1;transform:translateY(0%);}`
]);
const outAnimation = keyframes([
  `from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translateY(100%);}`
]);
const ModalDrawerContentContainerVariants = {
  default: css([
    `background:`,
    `;&:after{background:`,
    `;}`
  ], cssVar["color-bg-primary"], cssVar["color-bg-primary"]),
  extraPanel: css([
    `background:`,
    `;&:after{background:`,
    `;}`
  ], cssVar["bg-expanded-resource-extra-panel"], cssVar["bg-expanded-resource-extra-panel"])
};
const ModalDrawerContentContainer = styled(Content).attrs((props) => {
  return {
    ...defaultModalContentProps,
    ...props
  };
}).withConfig({
  displayName: "Drawer__ModalDrawerContentContainer",
  componentId: "sc-512cd008-0"
})([
  `--drawer-height:50vh;--drawer-pan-value:0px;display:flex;flex-direction:column;border-radius:2rem 2rem 0 0;height:calc(var(--drawer-height) - var(--drawer-pan-value));max-height:calc( 100% - env(safe-area-inset-top) - max(var(--keyboard-height,0px),env(safe-area-inset-bottom)) );will-change:height;position:fixed;bottom:max(var(--keyboard-height,0px),env(safe-area-inset-bottom));&:after{content:'';position:absolute;top:100%;display:block;height:100px;width:100%;left:0;}`,
  `{&:after{content:'';position:absolute;top:100%;display:block;height:100px;width:100%;left:0;background-color:inherit;z-index:1;}}left:0;width:100vw;z-index:125;animation-fill-mode:forwards;transform:translateY(100%) translateZ(0.01px);opacity:0;`,
  ` `,
  `;`
], Modal.Footer, (p) => !p.disableAnimation ? css([
  `&[data-state='open']{animation:`,
  ` 0.2s ease-out forwards;animation-delay:200ms;}&[data-state='closed']{animation:`,
  ` 0.2s ease-out forwards;}`
], inAnimation, outAnimation) : css([
  `opacity:1;transform:translateY(0) translateZ(0.01px);`
]), (p) => ModalDrawerContentContainerVariants[p.variant || "default"]);
const DrawerTitle = styled.div.withConfig({
  displayName: "Drawer__DrawerTitle",
  componentId: "sc-512cd008-1"
})([
  `font-weight:600;font-size:1.25rem;line-height:1.5rem;letter-spacing:-0.45px;color:`,
  `;padding:0 1rem;`
], cssVar["color-text-primary"]);
const DrawerDragHandle = styled(motion.div).withConfig({
  displayName: "Drawer__DrawerDragHandle",
  componentId: "sc-512cd008-2"
})([
  `z-index:1;height:7px;position:relative;flex-shrink:0;touch-action:none;`,
  `{display:none;}`,
  `{& + *{margin-top:1.25rem;}}&:after{content:'';display:block;position:absolute;background:red;width:100%;height:var(--drag-handle-hitbox-height,70px);top:-10px;opacity:0;touch-action:none;}&:before{content:'';display:block;position:absolute;top:0;left:50%;transform:translateX(-50%);background:`,
  `;height:7px;width:48px;border-radius:3.5px;z-index:2;}[data-deprecated-padding='true'] &{height:23px;&:before{top:1rem;}}`
], mediaNotMobile, mediaMobile, cssVar["color-bg-quinary"]);
const DEFAULT_HEIGHT = "60dvh";
const EXPANDED_HEIGHT = "100dvh";
const ModalDrawerContent = /* @__PURE__ */ React__default.forwardRef(({ children, onClose, as, baseHeight = DEFAULT_HEIGHT, panMode = "handle", variant, disableAnimation, forceExpanded, defaultExpanded, respectContentHeight = false, dragHandleHitboxHeight, deprecatedPadding = false }, fref) => {
  const { value: expandedState, set: setExpandedState } = useBoolState(forceExpanded || defaultExpanded || false);
  const contentRef = useRef(null);
  const minHeight = useMinContentHeight(contentRef, !respectContentHeight);
  const currentHeight = useMemo(() => {
    return expandedState || forceExpanded ? EXPANDED_HEIGHT : `max(${baseHeight}, ${minHeight ?? 0}px)`;
  }, [
    expandedState,
    forceExpanded,
    baseHeight,
    minHeight
  ]);
  const ref = useMemo(() => mergeRefs([
    fref,
    contentRef
  ]), [
    fref
  ]);
  const [panValue, setPanValue] = React__default.useState(0);
  const isPanningState = useBoolState();
  const handlePanStart = () => {
    isPanningState.handleTrue();
  };
  const handlePan = (delta) => {
    setPanValue((prev) => prev + delta);
  };
  const handlePanEnd = (direction, velocity, offset) => {
    isPanningState.handleFalse();
    setPanValue(0);
    if (direction === "up" && (velocity > 0.5 || offset < -80)) {
      setExpandedState(true);
    } else if (direction === "down" && (velocity > 0.5 || offset > 80)) {
      if (currentHeight === EXPANDED_HEIGHT) {
        setExpandedState(false);
      } else {
        setExpandedState(false);
        onClose();
      }
    }
  };
  useWindowPanning({
    onPanStart: handlePanStart,
    onPan: handlePan,
    onPanEnd: handlePanEnd,
    respectScrollAreas: currentHeight === EXPANDED_HEIGHT ? true : "up",
    disabled: panMode !== "window"
  });
  return /* @__PURE__ */ jsx(Fragment, {
    children: /* @__PURE__ */ jsxs(ModalDrawerContentContainer, {
      "data-deprecated-padding": deprecatedPadding,
      style: {
        //        height,
        transition: isPanningState.value ? void 0 : "height 0.1s ease-out",
        "--drag-handle-hitbox-height": dragHandleHitboxHeight,
        "--drawer-height": currentHeight,
        "--drawer-pan-value": `${forceExpanded ? 0 : panValue}px`
      },
      as,
      ref,
      variant,
      disableAnimation,
      children: [
        /* @__PURE__ */ jsx(DrawerDragHandle, {
          draggable: false,
          onPanStart: () => {
            isPanningState.handleTrue();
          },
          onPan: (e, info) => {
            setPanValue(info.offset.y);
          },
          onPanEnd: (e, info) => {
            isPanningState.handleFalse();
            setPanValue(0);
            if (info.offset.y < -80) {
              setExpandedState(true);
              return;
            }
            if (info.offset.y > 80) {
              if (currentHeight === EXPANDED_HEIGHT) {
                setExpandedState(false);
              } else {
                setExpandedState(false);
                onClose();
              }
            }
          }
        }),
        children
      ]
    })
  });
});
ModalDrawerContent.displayName = "ModalDrawerContent";

export { DrawerDragHandle, DrawerTitle, ModalDrawerContent };
//# sourceMappingURL=Drawer.js.map
